/*
* Created on Feb 02, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import {useState, useCallback, useEffect} from 'react';
import useToggle from '../../../hook/ToggleHook';
import ic_pagination_front_svg from '../../../static/img/utils/ic-pagination-front.svg';
import ic_pagination_prev_svg from '../../../static/img/utils/ic-pagination-prev.svg';
import ic_pagination_next_svg from '../../../static/img/utils/ic-pagination-next.svg';
import ic_pagination_last_svg from '../../../static/img/utils/ic-pagination-last.svg';
import ic_pagination_refresh_svg from '../../../static/img/utils/ic-pagination-refresh.svg';
import {useTranslation} from "react-i18next";

const TableContentPagination = ({total_content, table_data, disPatcher}) => {
    const {t} = useTranslation();
    const [contentPerPage, setContentPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [reachStart, setReachStart] = useState(true);
    const reachEnd = useCallback(() => total_content / (contentPerPage * currentPage) <= 1, [total_content, currentPage, contentPerPage]);
    const totalPage = useCallback(()=>{
        if (!total_content || total_content <= 0) return 1;
        return Math.ceil( total_content/contentPerPage);
    }, [total_content, contentPerPage])
    const [open, node] = useToggle()

    const checkArrowStatus = (curPage) => {
        reachEnd();
        setReachStart(curPage <= 1);

    }

    const next = () => {
        if (reachEnd()) return;
        const total = totalPage();
        setCurrentPage((prevPage) => {
            checkArrowStatus(prevPage+1);
            return Math.min(prevPage+1, total);
        });
        const offset = currentPage * contentPerPage
        disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: offset}});
    }

    const prev = () => {
        if (reachStart) return;
        setCurrentPage((prevPage) => {
            const newVal = Math.max(prevPage-1, 1);
            const offset = (newVal - 1) * contentPerPage
            disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: offset}});
            checkArrowStatus(newVal);
            return Math.max(prevPage-1, 1);
        });
    }

    const first = () => {
        setCurrentPage(1);
        checkArrowStatus(1);
        disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: 0}});
    }

    const last = () => {
        const total = totalPage();
        setCurrentPage(total);
        const offset = (total - 1) * contentPerPage
        // setReachStart(false);
        // setReachEnd(true);
        checkArrowStatus(total);
        disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: offset}});
    }

    const jump = (page) => {
        const total = totalPage();
        setCurrentPage(page);
        if (page < 1 || page > total) return;
        const offset = (page - 1) * contentPerPage
        checkArrowStatus(page);
        disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: offset, refresh: true}});
    }

    const changeSize = (size, page) => {
        const total = totalPage();
        page = page > total ? total : page;
        setCurrentPage(page);
        if (page < 1 || page > total) return;
        const offset = (page - 1) * contentPerPage
        checkArrowStatus(page);
        disPatcher({type: 'pagination', payload: {limit: size, offset: offset, refresh: true}});
    }

    // const refresh = (page) => {
    //     const total = totalPage();
    //     setCurrentPage(page);
    //     if (page < 1 || page > total) return;
    //     checkArrowStatus(page);
    //     disPatcher({type: 'pagination', payload: {limit: contentPerPage, offset: offset}});
    // }

    const on_current_page_blur = () => {
        const total = totalPage();
        if (currentPage < 1 || currentPage > total || !currentPage) {
            first();
        }
    }

    useEffect(()=>{
        checkArrowStatus(currentPage);
    }, [reachStart, currentPage, contentPerPage]);

    useEffect(()=>{
        changeSize(contentPerPage, currentPage);
    }, [contentPerPage]);


    return (
        <div className="pagination-bar">
            <div className="pagination-bar-wrapper">
                <div className="pagination">
                    <span className={`pagination-btn ${reachStart ? 'disabled' : ''}`} onClick={first}><img src={ic_pagination_front_svg} alt="" /></span>
                    <span className={`pagination-btn ${reachStart ? 'disabled' : ''}`} onClick={prev}><img src={ic_pagination_prev_svg} alt="" /></span>
                    <span className="pagination-divide"/>
                    <span className="text-page">{t('@Page@')}</span>
                    <input type="text" value={currentPage}
                           onChange={(e)=>jump(e.currentTarget.value)}
                           onBlur={on_current_page_blur}
                    />
                    <span className="text-page">/ {totalPage()}</span>
                    <span className="pagination-divide"/>
                    <span className={`pagination-btn ${reachEnd() ? 'disabled' : ''}`} onClick={next}><img src={ic_pagination_next_svg} alt="" /></span>
                    <span className={`pagination-btn ${reachEnd() ? 'disabled' : ''}`} onClick={last}><img src={ic_pagination_last_svg} alt="" /></span>
                    <span className="pagination-divide" />
                    <span className="pagination-btn" onClick={()=>jump(currentPage)}><img src={ic_pagination_refresh_svg} alt="" /></span>
                </div>
                <div className="pagination">
                    <span className="text-page">{t('@Display item@')}：{(currentPage-1) * contentPerPage + 1} - {(currentPage-1) * contentPerPage + table_data.length}</span>
                    <span className="pagination-divide" />
                    <span className="text-page">{t('@Show@')}</span>
                    <div className="comp-select dir-up" ref={node}>
                        <div className="select-toggle">
                            <span className="select-label">{contentPerPage}</span>
                        </div>
                        <div className="comp-dropdown comp-select-dropdown">
                            <ul className={`comp-dropdown-menu ${open}`}>
                                <li className="section">
                                    <div className="section-items selection" onClick={()=>setContentPerPage(10)}>10</div>
                                    <div className="section-items selection" onClick={()=>setContentPerPage(20)}>20</div>
                                    <div className="section-items selection" onClick={()=>setContentPerPage(50)}>50</div>
                                    <div className="section-items selection" onClick={()=>setContentPerPage(100)}>100</div>
                                    <div className="section-items selection" onClick={()=>setContentPerPage(150)}>150</div>
                                    <div className="section-items selection" onClick={()=>setContentPerPage(200)}>200</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <span className="text-page">{t('@Items per page@')}</span>
                </div>
            </div>
        </div>
    );
};

export default TableContentPagination;